import { useEffect, useState } from 'react'

import type { BillPaymentRound } from '@/models/bill/bill'
import { formatNumber } from '@/utils/number'
import { useGeneratePDF } from '@/modules/bill/services/bill'

type BillPdfRoundPreviewProps = {
  billId: string
  round: BillPaymentRound
  /** ซ่อนหัวซ้ำเมื่ออยู่ในแท็บ (มีปุ่มพิมพ์ด้านบนแล้ว) */
  compact?: boolean
}

export function BillPdfRoundPreview({ billId, round, compact = false }: BillPdfRoundPreviewProps) {
  const [pdfUrl, setPdfUrl] = useState('')

  const { data: pdfBlob, isPending, isError } = useGeneratePDF(billId, round.round_no)

  useEffect(() => {
    if (pdfBlob instanceof Blob) {
      const url = URL.createObjectURL(pdfBlob)
      setPdfUrl(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    }
    setPdfUrl('')
  }, [pdfBlob])

  return (
    <article className="flex w-full flex-col gap-2">
      {!compact ? (
        <header className="border-b border-border/70 pb-2">
          <h3 className="text-sm font-semibold text-foreground">ใบเสร็จรับเงิน — งวดที่ {round.round_no}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            ชำระ {formatNumber(round.paid_amount)} บาท ·{' '}
            {round.payment_method === 'cash' ? 'เงินสด' : 'โอน'} · คงเหลือ{' '}
            {formatNumber(round.remaining_after)} บาท
          </p>
        </header>
      ) : (
        <p className="text-xs text-muted-foreground">
          ชำระ {formatNumber(round.paid_amount)} บาท · คงเหลือ {formatNumber(round.remaining_after)} บาท
        </p>
      )}

      {isPending ? (
        <div className="flex min-h-[min(50vh,480px)] items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 text-sm text-muted-foreground">
          กำลังโหลด PDF…
        </div>
      ) : isError ? (
        <div className="flex min-h-[120px] items-center justify-center rounded-xl border border-destructive/40 bg-destructive/5 px-4 text-center text-sm text-destructive">
          โหลด PDF ไม่สำเร็จ
        </div>
      ) : pdfUrl ? (
        <div className="w-full overflow-hidden rounded-xl border border-border bg-muted/15 shadow-sm">
          <iframe
            src={pdfUrl}
            title={`ใบเสร็จงวดที่ ${round.round_no}`}
            className="min-h-[min(50vh,480px)] w-full border-0"
          />
        </div>
      ) : null}
    </article>
  )
}
